import { useMemo, useState } from 'react';
import type { ProposalProductItemType, ProductType } from '@econtractor/types';
import * as apiProductSet from 'api/apiProductSets';
import type { ProductSelectOption, ProductSetSelectOption } from 'modules/product';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing';
import { getFmtRate } from '../../model/templatePriceUtils';
import mergeProductRows, { type ReplacedProductItem } from './mergeProductRows';

export type SetAddMode = 'products' | 'set';

export interface UseTemplateProductsParams {
    products: ProposalProductItemType[];
    productsMap: Record<number, ProductType>;
    rate: number;
    onChange: (products: ProposalProductItemType[]) => void;
}

const useTemplateProducts = ({
    products,
    productsMap,
    rate,
    onChange,
}: UseTemplateProductsParams) => {
    const [sourceType, setSourceTypeState] = useState<'product' | 'set'>('product');
    const [setAddMode, setSetAddMode] = useState<SetAddMode>('products');
    const [selectedProduct, setSelectedProduct] = useState<ProductSelectOption | null>(null);
    const [selectedSet, setSelectedSet] = useState<ProductSetSelectOption | null>(null);
    const [draftCount, setDraftCount] = useState(1);
    const [isAdding, setIsAdding] = useState(false);
    const [replacedAlert, setReplacedAlert] = useState<ReplacedProductItem[] | null>(null);

    const canAdd = draftCount > 0 && (sourceType === 'product' ? !!selectedProduct?.value : !!selectedSet?.value);
    const fmtRate = useMemo(() => getFmtRate(rate), [rate]);

    const setSourceType = (next: 'product' | 'set') => {
        setSourceTypeState(next);
        setSetAddMode('products');
    };

    const dismissReplacedAlert = () => {
        setReplacedAlert(null);
    };

    const pushRows = (
        rows: ProposalProductItemType[],
        opts?: Parameters<typeof mergeProductRows>[2],
    ) => {
        if (!rows.length) return;
        const { products: nextProducts, replaced } = mergeProductRows(products, rows, opts);
        onChange(nextProducts);
        setReplacedAlert(replaced.length ? replaced : null);
    };

    const handleAddProduct = () => {
        if (!selectedProduct?.value || draftCount <= 0) return;
        const price = safeFloatParse(selectedProduct.defPrice || 0) || 0;
        const priceSale = safeFloatParse(selectedProduct.defPriceSale || price) || 0;
        pushRows([
            {
                rowId: `${Date.now()}-${selectedProduct.value}`,
                sourceType: 'product',
                sourceId: selectedProduct.value,
                sourceName: selectedProduct.label,
                productId: selectedProduct.value,
                itemCount: Math.max(0, Math.floor(draftCount) || 0),
                price,
                priceRate: safeFloatParse(price * fmtRate) || 0,
                priceSale,
                priceSaleRate: safeFloatParse(priceSale * fmtRate) || 0,
                prodName: selectedProduct.label,
                prodArticle: selectedProduct.article,
                vendorName: selectedProduct.vendor,
                dimensionName: selectedProduct.dimensionName || '',
                dimensionI18nKey: selectedProduct.dimensionI18nKey || '',
            },
        ]);
        setSelectedProduct(null);
        setDraftCount(1);
    };

    const handleAddSetAsProducts = async () => {
        if (!selectedSet?.value || draftCount <= 0) return;
        const setData = await apiProductSet.getProductSet(selectedSet.value);
        if (!setData?.items?.length) return;

        const rows = setData.items
            .map((setItem) => {
                const productId = safeIntParse(setItem.productId) || 0;
                const product = productsMap[productId];
                const basePrice = safeFloatParse(product?.prodDefPrice || 0) || 0;
                const basePriceSale = safeFloatParse(product?.prodDefPriceSale || basePrice) || 0;
                return {
                    rowId: `${Date.now()}-${selectedSet.value}-${setItem.productId}`,
                    sourceType: 'set' as const,
                    sourceId: selectedSet.value,
                    sourceName: selectedSet.label,
                    productId,
                    itemCount: Math.max(
                        0,
                        Math.floor((safeFloatParse(setItem.itemCount) || 0) * (Math.floor(draftCount) || 0)),
                    ),
                    price: basePrice,
                    priceRate: safeFloatParse(basePrice * fmtRate) || 0,
                    priceSale: basePriceSale,
                    priceSaleRate: safeFloatParse(basePriceSale * fmtRate) || 0,
                    prodName: setItem.prodName || product?.prodName || '',
                    prodArticle: setItem.prodArticle || product?.prodArticle || '',
                    vendorName: setItem.vendorName || product?.vendorName || '',
                    dimensionName: setItem.dimensionName || product?.dimensionName || '',
                    dimensionI18nKey: setItem.dimensionI18nKey || product?.dimensionI18nKey || '',
                };
            })
            .filter((row) => row.productId > 0 && row.itemCount > 0);

        pushRows(rows);
        setSelectedSet(null);
        setDraftCount(1);
    };

    const handleAddSetAsUnit = async () => {
        if (!selectedSet?.value || draftCount <= 0) return;
        const setData = await apiProductSet.getProductSet(selectedSet.value);
        if (!setData) return;

        const price = safeFloatParse(setData.totalPrice || 0) || 0;
        const priceSale = safeFloatParse(setData.totalPriceSale || price) || 0;
        const setName = selectedSet.label || setData.setName || `#${selectedSet.value}`;
        const removeProductIds = (setData.items || [])
            .map((item) => safeIntParse(item.productId) || 0)
            .filter((productId) => productId > 0);

        pushRows(
            [
                {
                    rowId: `${Date.now()}-set-${selectedSet.value}`,
                    sourceType: 'set',
                    sourceId: selectedSet.value,
                    sourceName: setName,
                    productId: 0,
                    asSetUnit: true,
                    itemCount: Math.max(0, Math.floor(draftCount) || 0),
                    price,
                    priceRate: safeFloatParse(price * fmtRate) || 0,
                    priceSale,
                    priceSaleRate: safeFloatParse(priceSale * fmtRate) || 0,
                    prodName: setName,
                    prodArticle: setData.setArticle || selectedSet.article || '',
                    dimensionName: setData.dimensionName || '',
                    dimensionI18nKey: setData.dimensionI18nKey || '',
                },
            ],
            {
                removeProductIds,
                removeSetUnitSourceIds: [selectedSet.value],
            },
        );
        setSelectedSet(null);
        setDraftCount(1);
    };

    const handleAddSet = async () => {
        if (isAdding) return;
        setIsAdding(true);
        try {
            if (setAddMode === 'set') {
                await handleAddSetAsUnit();
                return;
            }
            await handleAddSetAsProducts();
        } finally {
            setIsAdding(false);
        }
    };

    const handleDraftAdd = () => {
        if (!canAdd || isAdding) return;
        if (sourceType === 'product') handleAddProduct();
        else void handleAddSet();
    };

    const setDraftCountDown = () => {
        setDraftCount((prev) => Math.max(1, Math.floor(prev) - 1));
    };

    const setDraftCountUp = () => {
        setDraftCount((prev) => Math.floor(prev) + 1);
    };

    const setDraftCountFromInput = (value: string) => {
        const parsed = parseInt(value, 10);
        setDraftCount(Number.isFinite(parsed) ? Math.max(0, parsed) : 0);
    };

    return {
        sourceType,
        setSourceType,
        setAddMode,
        setSetAddMode,
        selectedProduct,
        setSelectedProduct,
        selectedSet,
        setSelectedSet,
        draftCount,
        setDraftCountDown,
        setDraftCountUp,
        setDraftCountFromInput,
        canAdd,
        isAdding,
        handleDraftAdd,
        replacedAlert,
        dismissReplacedAlert,
    };
};

export default useTemplateProducts;
